import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { API_URL } from "../../Constants";
import { toast } from "react-toastify";
import { PlusIcon, ClipboardIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import ConfirmModal from "./ConfirmModal";
import LeadFieldMappingEditor from "./LeadFieldMappingEditor";

const WEBHOOK_URL = `${API_URL}/api/ivr/call-record`;

const maskKey = (key) => {
  if (!key) return "";
  if (key.length <= 8) return "••••••••";
  return `${key.slice(0, 6)}••••••••${key.slice(-4)}`;
};

const copyToClipboard = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Could not copy to clipboard");
  }
};

const WebhookIntegrations = () => {
  const { user } = useContext(AuthContext);
  const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
  const companyId = user?.accountType === "COMPANY" ? user?.id : user?.companyId;

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newEndpointKey, setNewEndpointKey] = useState("");
  const [creating, setCreating] = useState(false);

  const [busyId, setBusyId] = useState(null); // id currently being rotated/toggled
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [expandedKeyId, setExpandedKeyId] = useState(null);

  useEffect(() => {
    if (companyId) fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/ivr/webhook-keys/company/${companyId}`, { headers: authHeader });
      setKeys(res.data || []);
    } catch {
      toast.error("Failed to load webhook integrations");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setCreating(true);
      await axios.post(
        `${API_URL}/api/ivr/webhook-keys`,
        { companyId, endpointKey: newEndpointKey.trim() || undefined, label: newLabel.trim() || undefined },
        { headers: authHeader }
      );
      toast.success("Webhook integration created");
      setNewLabel("");
      setNewEndpointKey("");
      setShowAddForm(false);
      await fetchKeys();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to create webhook integration");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (key) => {
    try {
      setBusyId(key.id);
      await axios.put(
        `${API_URL}/api/ivr/webhook-keys/${key.id}/active?companyId=${companyId}&active=${!key.active}`,
        {},
        { headers: authHeader }
      );
      await fetchKeys();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update integration");
    } finally {
      setBusyId(null);
    }
  };

  const handleRotate = async (key) => {
    try {
      setBusyId(key.id);
      await axios.put(`${API_URL}/api/ivr/webhook-keys/${key.id}/rotate?companyId=${companyId}`, {}, { headers: authHeader });
      toast.success("Key rotated - update it with your vendor now, the old key stops working immediately");
      await fetchKeys();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to rotate key");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await axios.delete(`${API_URL}/api/ivr/webhook-keys/${keyToDelete.id}?companyId=${companyId}`, {
        headers: authHeader,
      });
      toast.success("Webhook integration deleted");
      setKeys((prev) => prev.filter((k) => k.id !== keyToDelete.id));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete integration");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
      setKeyToDelete(null);
    }
  };

  return (
    <div>
      <div className="mb-6 p-4 bg-[#F3E8FF] dark:bg-blue-900 rounded-lg">
        <h3 className="font-semibold cfg-heading text-[#673AB7] dark:text-blue-200 mb-2">How this works</h3>
        <p className="text-sm text-[#5E35A6] dark:text-blue-100 mb-3">
          Each integration below is a separate key for a vendor to authenticate calls sent to your CRM. Give
          the vendor the URL and key, and their calls will create or update leads automatically.
        </p>
        <div className="flex items-center gap-2 text-sm bg-white dark:bg-gray-800 rounded px-3 py-2 mb-2">
          <span className="text-gray-500">URL:</span>
          <code className="flex-1 truncate">{WEBHOOK_URL}</code>
          <button onClick={() => copyToClipboard(WEBHOOK_URL, "Webhook URL")} title="Copy">
            <ClipboardIcon className="h-4 w-4 text-gray-500 hover:text-[#673AB7]" />
          </button>
        </div>
        <p className="text-xs text-[#673AB7] dark:text-blue-200">
          The vendor sends the key in an <code>X-API-KEY</code> header on every request.
        </p>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h4 className="text-sm font-semibold cfg-heading">Your webhook integrations</h4>
        <button
          onClick={() => setShowAddForm((v) => !v)}
          className="flex items-center px-3 py-2 text-sm bg-[#673AB7] text-white rounded hover:bg-[#5E35A6]"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add integration
        </button>
      </div>

      {showAddForm && (
        <div className="mb-6 p-4 bg-white dark:bg-gray-800 rounded border grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium mb-1">Label</label>
            <input
              placeholder="e.g. Vendor A - Delhi office"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="w-full p-2 border border-[#E5E7EB] rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Endpoint key (optional)</label>
            <input
              placeholder="default"
              value={newEndpointKey}
              onChange={(e) => setNewEndpointKey(e.target.value)}
              className="w-full p-2 border border-[#E5E7EB] rounded dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#E9D5FF] focus:border-[#673AB7]"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={creating}
            className="px-4 py-2 bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50"
          >
            {creating ? "Creating..." : "Create"}
          </button>
        </div>
      )}

      {loading && <div className="text-sm text-gray-600">Loading...</div>}

      {!loading && keys.length === 0 && (
        <div className="p-6 text-center text-sm text-gray-500 border rounded bg-white dark:bg-gray-800">
          No webhook integrations yet. Add one to get a URL and key you can hand to any vendor.
        </div>
      )}

      {keys.map((key) => {
        const isExpanded = expandedKeyId === key.id;
        return (
          <div key={key.id} className="mb-3 bg-white dark:bg-gray-800 rounded border">
            <div className="p-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{key.label || key.endpointKey}</span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      key.active
                        ? "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200"
                        : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {key.active ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                  <span>endpointKey: {key.endpointKey}</span>
                  <span>•</span>
                  <span>{maskKey(key.apiKey)}</span>
                  <button onClick={() => copyToClipboard(key.apiKey, "API key")} title="Copy key">
                    <ClipboardIcon className="h-3.5 w-3.5 text-gray-400 hover:text-[#673AB7]" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpandedKeyId(isExpanded ? null : key.id)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Configure mapping
                  {isExpanded ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                </button>
                <button
                  onClick={() => handleToggleActive(key)}
                  disabled={busyId === key.id}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  {key.active ? "Disable" : "Enable"}
                </button>
                <button
                  onClick={() => handleRotate(key)}
                  disabled={busyId === key.id}
                  className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Rotate key
                </button>
                <button
                  onClick={() => {
                    setKeyToDelete(key);
                    setShowDeleteModal(true);
                  }}
                  className="px-3 py-1.5 text-sm bg-red-50 text-red-600 dark:bg-red-900 dark:text-red-200 rounded hover:bg-red-100 dark:hover:bg-red-800"
                >
                  Delete
                </button>
              </div>
            </div>

            {isExpanded && (
              <div className="px-4 pb-4">
                <LeadFieldMappingEditor ingestionPoint="IVR" endpointKey={key.endpointKey} />
              </div>
            )}
          </div>
        );
      })}

      <ConfirmModal
        open={showDeleteModal}
        title="Delete webhook integration?"
        message={`This immediately stops "${keyToDelete?.label || keyToDelete?.endpointKey}" from authenticating. Any vendor still using this key will start getting rejected requests.`}
        confirmLabel="Delete"
        loading={deleting}
        onCancel={() => {
          setShowDeleteModal(false);
          setKeyToDelete(null);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default WebhookIntegrations;
